import $ from 'jquery'
import numeral from 'numeral'
import { capitalize } from 'src/utils/formatters'
import DataUtils from 'src/utils/data'
import _ from 'lodash'
import escapeRegExp from 'escape-string-regexp'
import { SchemaColumn } from 'types/SchemaTypes'
import { DashboardFilterType } from 'src/types/DashboardFilters.types'

const uuid = function b (a?: any): string {
  return a ? (a ^ Math.random() * 16 >> a / 4).toString(16) : (([1e7] as any) + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, b) // njsscan-ignore: node_insecure_random_generator
}

// DO NOT CHANGE THIS BLOCK! IT IS USED FOR SHARE LINK GENERATION!
const Hashids = require('hashids/cjs')
const hashids = new Hashids(
  'NgJTH4cX4yDzCsu',  // Salt
  8,  // Length
  'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'  // Vocab  nosec
)

// Note: cannot specify the "u" for unicode regexp in IE, so don't use "ug" at the
// end of the unicode literal. The regexp below has been tested to work, including the
// unicode ranges, on both IE11 and Firefox.
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/RegExp/unicode#Browser_compatibility
const PUNCTUATION_RE_OPENING = new RegExp(/[\u2000-\u206F\u2E00-\u2E7F\\'!%()+,\-\/:;<=>?@\[\]^_`{|}~]+/g)
const PUNCTUATION_RE_CLOSING = new RegExp(/[\u2000-\u206F\u2E00-\u2E7F\\'!#$%()+,\-\/:;<=>?@\[\]^_`{|}~]+/g)


const LINES_WITH_CONTENT_RE = /^\s*\S/gm
const SEGMENTS_COMMA_RE = /([^\,]+)(\,?)/g
const SEGMENTS_LINE_RE = /(.+)(\r?\n|$)/g

// Copy-pasta from MDN ¯\_(ツ)_/¯
// https://developer.mozilla.org/en-US/docs/Web/API/WindowBase64/Base64_encoding_and_decoding
function b64EncodeUnicode (str: string) {
  // first we use encodeURIComponent to get percent-encoded UTF-8,
  // then we convert the percent encodings into raw bytes which
  // can be fed into btoa.
  return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g,
    function toSolidBytes (match, p1) {
      return String.fromCharCode('0x' + p1 as any)
    })
  )
}
function b64DecodeUnicode (str: string) {
  // Going backwards: from bytestream, to percent-encoding, to original string.
  return decodeURIComponent(atob(str).split('').map(function (c) {
    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
  }).join(''))
}

// Fix 'Array.flat' in IE11, which should be handled by Babel but doesn't seem to work in v6.
// Should be able to remove this once upgraded to v7.
if (!Array.prototype.flat) {
  Array.prototype.flat = function () {
    return [].concat(...this as any[])
  }
}

export async function sleep (milliseconds: number) {
  await new Promise(resolve => setTimeout(resolve, milliseconds))
}

function downloadFile (data: any, filename: string, extension: string) {
  let blob = new window.Blob([data], { type: 'text/csv' })
  filename = `${filename}.${extension}`
  if (navigator.msSaveBlob) { // Needed for IE 10+ which has size limit on URL
    navigator.msSaveBlob(blob, filename)
  } else {
    let objectUrl = window.URL.createObjectURL(blob)
    let link = document.createElement('a')
    document.body.appendChild(link)
    link.style.display = 'none'
    link.href = objectUrl
    link.download = filename
    link.click()
    link.remove()
    window.URL.revokeObjectURL(objectUrl)
  }
}

export default {
  ARROW_CHAR: '⇨',
  DATA_UNITS_CHANGE_BLOG_URL: 'https://medium.com/p/3de00784b2cf/',
  // Returns a list of sorted indices for `arr`, optionally apply `abs` (absolute) function.
  // Behaves similarly to numpy.argsort()
  argSort (arr: string[] | number[], abs: boolean) {
    let indices = Array.from(arr.keys())
    indices.sort(function (i1, i2) {
      if (abs) {
        return Math.abs(+arr[i2]) - Math.abs(+arr[i1])
      } else {
        if ((arr[i2] as string).localeCompare) {
          // Handle string comparison
          return (arr[i2] as string).localeCompare(<string>arr[i1])
        }
        return +arr[i2] - +arr[i1]
      }
    })
    return indices
  },

  // Check a schema has a date field
  checkSchemaHasDate (schema: SchemaColumn[]) {
    for (let field of schema) {
      if (field.type === 3 || field.type === 4) {
        return true
      }
    }
    return false
  },
  // Does a schema have an NPS column?
  checkNpsExists (schema: SchemaColumn[]) {
    for (let field of schema) {
      if (field.type === 7) {  // Is there an NPS field?
        return true
      }
    }
    return false
  },
  getDateFields (schema: SchemaColumn[]) {
    return schema.filter(({type}) => type === 3 || type === 4)
  },
  getNpsFields (schema: SchemaColumn[]) {
    const npsFields = []
    for (let field of schema) {
      if (field.type === 7) {
        npsFields.push(field)
      }
    }
    return npsFields
  },
  /**
   * This helper method simply converts a list of filters into an accessor for the caching dictionary
   * It sorts separate filters and concatenates them on a control character
   * @param {array} filters - the list of filters to convert into an accessor
   */
  getFilterKey (filters: DashboardFilterType[]) {
    if (!filters) {
      return ''
    }
    let filterLabels = filters.map(val => val.field + this.ARROW_CHAR + val.segment)
    return filterLabels.join(DataUtils.INVISIBLE_PREFIX)
  },
  // This is a method to help sort an object into a sorted array of arrays, where subarray[0] is the key and subarray[1] is the value
  objectToArrays (object: Record<string, any>) {
    let arrays = []
    for (let concept in object) {
      arrays.push([concept, object[concept]])
    }
    return arrays
  },
  /**
   * A helper method to make a query into a string which is safe to embed in a html class attribute.
   * This function is used to create a class identifier for a string which potentially has non-html-class
   * -safe characters (/, ', etc.). It allows us to easily turn things such as query names into html classes
   * which we can select to change element attributes.
   * @param {String} query - the query to be made into a class safe string.
   * @return {String} - A hashed representation of the string using only html safe characters.
   */
  createClassHash (query: string) {
    if (typeof query !== 'string') { // fail gracefully
      return ''
    }
    let hex = new Buffer(query).toString('hex')
    return hashids.encodeHex(hex)
  },
  // Initiate client download of CSV with `filename` from `csvString` data.
  downloadCsv (csvString: string, filename: string) {
    // Note the UTF BOM \ufeff prepended to the file data. This ensures
    // that consumers of the data will detect the encoding correctly.
    // See: https://stackoverflow.com/a/17879474/170656
    downloadFile('\ufeff' + csvString, filename, 'csv')
  },

  // Initiate client download of TXT with `filename` from `txtString` data.
  downloadTxt (txtString: string, filename: string) {
    downloadFile(txtString, filename, 'txt')
  },

  generateSegmentLabel (variable: string, segment: string, truncate = 25) {
    segment = this.labelOrNoValue(segment)
    if (truncate) {
      variable = this.truncateText(variable, truncate)
      segment = this.truncateText(segment, truncate)
    }
    return `${variable}: ${segment}`
  },

  labelOrNoValue (label: string) {
    return (label && label.trim()) ? label : '(No value)'
  },

  generateSegmentName (variable: string, segment: string) {
    segment = this.labelOrNoValue(segment)
    return `${variable}=${segment}`
  },

  splitSegmentName (segmentName: string) {
    return segmentName.split('=')
  },

  generateSegmentFilterCountLabel (length: number) {
    if (!length) {
      return 'No filters'
    } else if (length === 1) {
      return '1 filter'
    } else {
      return `${length} filters`
    }
  },

  // Generate link to browse excerpts for query object
  generateQueryLink (query: any) {
    return {
      name: 'browse-excerpts',
      query: {
        q: this.generateEncodedQuery(query)
      }
    }
  },

  // Generate a url-ready encoded version of the query object
  generateEncodedQuery (query: any) {
    return b64EncodeUnicode(JSON.stringify(query))
  },

  generateConceptTooltip (topic: any, sentiment: any, colour: string) {
    let tip = `<div style="width:225px">
      <div style="font-size:13px;color:#95a6ac">Click to query for concept:</div>
      <h4 style="margin-top: 0"><i class="small icon circle" style="color:${colour}"></i> ${this.truncateText(topic.label, 30)}</h4>
    ` +
      `<table style="border-top: 1px solid rgba(149,166,172,0.5); margin-top: 10px; padding-top: 10px">` +
      `<tr><td>Verbatims</td><td style="font-weight:bold">${topic.frequency}</td>` +
      `<tr><td>Coverage</td><td style="font-weight:bold">${numeral(topic.nonEmptyCoverage).format('0.00%')}</td></tr>`
    if (sentiment !== null && sentiment !== undefined) {
      for (let sentimentType of ['positive', 'negative', 'mixed', 'neutral']) {
        if (!sentiment.topics.hasOwnProperty(sentimentType)) {  // Safeguard for mixed
          tip += `<tr class="sentiment ${sentimentType}" style="font-weight:bold"><td>${capitalize(sentimentType)}</td><td>0%</td></tr>`
        } else {
          tip += `<tr class="sentiment ${sentimentType}" style="font-weight:bold"><td>${capitalize(sentimentType)}</td><td>${numeral(sentiment.topics[sentimentType][topic.name] / topic.frequency).format('0%')}</td></tr>`
        }
      }
    }
    tip += '</div></table>'
    return tip
  },

  generateConceptGroupTooltip (conceptNames: string[], colour: string) {
    return `<div style="width:270px">
      <div style="font-size:13px;color:#95a6ac">Click to query for concepts:</div>
      <div style="font-size: 16px"><i class="small icon circle" style="color:${colour}"></i> ${this.truncateText(conceptNames.join(', '), 33)}</div>
    </div>`
  },

  overlayMask (container: HTMLElement, el: HTMLElement) {
    el.style.zIndex = '100'
    let canvas = document.createElement('canvas')
    canvas.className = 'highlight'
    canvas.width = $(document).width()!
    canvas.height = $(document).height()!
    let ctx = canvas.getContext('2d')!
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    container.appendChild(canvas)
    window.overlayCanvas = canvas
    window.overlayEl = el
  },

  closeOverlayMask () {
    if (window.overlayCanvas) {
      window.overlayCanvas.remove()
      delete window.overlayCanvas
      if (window.overlayEl) {
        window.overlayEl.style.zIndex = ''
        delete window.overlayEl
      }
    }
  },

  // Return a JSON version of encoded query.
  parseRouteQuery (query: any) {
    if (query) {
      try {
        return JSON.parse(b64DecodeUnicode(query))
      } catch (InvalidCharacterError) {
        // Couldn't parse query
        return []
      }
    }
    return []
  },

  /**
   * This is a helper method to recursively traverse a js object. It helps in the case where we need to set a nested value
   * and are unsure of the state of the sub-properties. For instance, if we have an empty object {} and need to set a deep property
   * where sub-objects may not exists and return a "Cannot set 'property' of undefined" error.
   * @param {String} propertyPath The path to the property you want to set as a string
   * @param {*} value The value to set at the key
   * @param {*} obj The object to mutate
   */
  setValue (propertyPath: string | string[], value: any, obj: any): boolean {
    // split by the dots at first and then simply pass along the array (on next iterations)
    let properties = Array.isArray(propertyPath) ? propertyPath : propertyPath.split('.')
    // Not yet at the last property so keep digging
    if (properties.length > 1) {
      // The property doesn't exists OR is not an object (and so we overwrite it) so we create it
      if (!obj.hasOwnProperty(properties[0]) || typeof obj[properties[0]] !== 'object') obj[properties[0]] = {}
      // Recursively call the function until we reach the place where we want to set the value
      return this.setValue(properties.slice(1), value, obj[properties[0]])
    } else { // This is the last property - the one where to set the value
      obj[properties[0]] = value
      return true
    }
  },

  // Truncate `text` to meet `maxChars`.
  // If text is undefined, return an empty string.
  truncateText (text: string, maxChars = 50) {
    text = (typeof text === 'undefined') ? '' : text
    text = (text === null) ? '' : text
    const suffix = ' ...'
    return text.length > maxChars ? (text.substring(0, maxChars - suffix.length) + suffix) : text
  },

  // Replace occurences of `term` in excerpt `text` with `replaceStr`.
  excerptTextTermReplace (text: string, term: string, replaceStr: string) {
    const escapedTerm = escapeRegExp(term)
    return text.replace(
      new RegExp('(?:\\b(' + escapedTerm + ')\\b)' +
        // Negative lookahead to avoid replacing html content
        // http://stackoverflow.com/a/18622606
        '(?![^<]*>|[^<>]*</)', 'g'
      ),
      replaceStr
    )
  },
  // Replace newlines (\r or \n) with a page break
  excerptNewlineReplace (text: string) {
    text = text.trim() // Trim leading and trailing whitespace
    return text.replace(
      new RegExp('\\n' +
        // Negative lookahead to avoid replacing html content
        // http://stackoverflow.com/a/18622606
        '(?![^<]*>|[^<>]*</)', 'g'),
      '<br>'
    )
  },

  /*
   * Takes current page (c) and max (m), and returns an array of pages
   * https://gist.github.com/kottenator/9d936eb3e4e3c3e02598
  Expected output:
  for (let i = 1, l = 20; i <= l; i++)
      console.log(`Selected page ${i}:`, pagination(i, l));
  Expected output:
  Selected page 1: [1, 2, 3, "...", 20]
  Selected page 2: [1, 2, 3, 4, "...", 20]
  Selected page 3: [1, 2, 3, 4, 5, "...", 20]
  Selected page 4: [1, 2, 3, 4, 5, 6, "...", 20]
  Selected page 5: [1, 2, 3, 4, 5, 6, 7, "...", 20]
  Selected page 6: [1, "...", 4, 5, 6, 7, 8, "...", 20]
  Selected page 7: [1, "...", 5, 6, 7, 8, 9, "...", 20]
  Selected page 8: [1, "...", 6, 7, 8, 9, 10, "...", 20]
  Selected page 9: [1, "...", 7, 8, 9, 10, 11, "...", 20]
  Selected page 10: [1, "...", 8, 9, 10, 11, 12, "...", 20]
  Selected page 11: [1, "...", 9, 10, 11, 12, 13, "...", 20]
  Selected page 12: [1, "...", 10, 11, 12, 13, 14, "...", 20]
  Selected page 13: [1, "...", 11, 12, 13, 14, 15, "...", 20]
  Selected page 14: [1, "...", 12, 13, 14, 15, 16, "...", 20]
  Selected page 15: [1, "...", 13, 14, 15, 16, 17, "...", 20]
  Selected page 16: [1, "...", 14, 15, 16, 17, 18, 19, 20]
  Selected page 17: [1, "...", 15, 16, 17, 18, 19, 20]
  Selected page 18: [1, "...", 16, 17, 18, 19, 20]
  Selected page 19: [1, "...", 17, 18, 19, 20]
  Selected page 20: [1, "...", 18, 19, 20]
  */
  pagination (c: number, m: number) {
    let current = c
    let last = m
    let delta = 2
    let left = current - delta
    let right = current + delta + 1
    let range = []
    let rangeWithDots = []
    let l

    for (let i = 1; i <= last; i++) {
      if (i === 1 || i === last || i >= left && i < right) {
        range.push(i)
      }
    }

    for (let i of range) {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1)
        } else if (i - l !== 1) {
          rangeWithDots.push('...')
        }
      }
      rangeWithDots.push(i)
      l = i
    }
    return rangeWithDots
  },

  /*!
  * natural-sort.js
  * ===============
  * Sorting with support for numbers, dates, unicode and more.
  *
  * http://github.com/studio-b12/natural-sort
  * MIT License, © Studio B12 GmbH 2014
  *
  *
  * Idea by Dave Koelle
  * Original implementation by Jim Palmer
  * Modified by Tomek Wiszniewski
  *
  */

  naturalSort (options: Record<string, any>) {
    if (!options) options = {}

    return function (a: number, b: number) {
      const EQUAL = 0
      const GREATER = (options.direction === 'desc' ? -1 : 1)
      const SMALLER = -GREATER

      const re = /(^-?[0-9]+(\.?[0-9]*)[df]?e?[0-9]?$|^0x[0-9a-f]+$|[0-9]+)/gi
      const sre = /(^[ ]*|[ ]*$)/g
      const dre = /(^([\w ]+,?[\w ]+)?[\w ]+,?[\w ]+\d+:\d+(:\d+)?[\w ]?|^\d{1,4}[\/\-]\d{1,4}[\/\-]\d{1,4}|^\w+, \w+ \d+, \d{4})/
      const hre = /^0x[0-9a-f]+$/i
      const ore = /^0/

      const normalize = function normalize (value: number) {
        const string = '' + value
        return (options.caseSensitive ? string : string.toLowerCase())
      }

      // Access fn
      if (options.accessor) {
        a = options.accessor(a)
        b = options.accessor(b)
      }

     // Return immediately if at least one of the values is empty.
      const aEmpty = !a && a !== 0
      const bEmpty = !b && b !== 0
      if (aEmpty && bEmpty) return EQUAL
      if (aEmpty && !bEmpty) return options.nullsFirst? GREATER: SMALLER
      if (!aEmpty && bEmpty) return options.nullsFirst? SMALLER: GREATER

      // Normalize values to strings
      const x = normalize(a).replace(sre, '') || ''
      const y = normalize(b).replace(sre, '') || ''

      // chunk/tokenize
      const xN = x.replace(re, '\0$1\0').replace(/\0$/, '').replace(/^\0/, '').split('\0')
      const yN = y.replace(re, '\0$1\0').replace(/\0$/, '').replace(/^\0/, '').split('\0')

      // numeric, hex or date detection
      let xD = parseInt(`${x.match(hre)}`)
      if (!xD && xN.length !== 1 && x.match(dre)) xD = Date.parse(x)
      let yD: number | null = parseInt(`${y.match(hre)}`)
      if (!yD && xD && y.match(dre)) yD = Date.parse(y) || null
      let oFxNcL, oFyNcL

      // first try and sort Hex codes or Dates
      if (yD) {
        if (xD! < yD) return SMALLER
        else if (xD! > yD) return GREATER
      }

      // natural sorting through split numeric strings and default strings
      for (let cLoc = 0, numS = Math.max(xN.length, yN.length); cLoc < numS; cLoc++) {
        // find floats not starting with '0', string or 0 if not defined (Clint Priest)
        oFxNcL = !(xN[cLoc] || '').match(ore) && parseFloat(xN[cLoc]) || xN[cLoc] || 0
        oFyNcL = !(yN[cLoc] || '').match(ore) && parseFloat(yN[cLoc]) || yN[cLoc] || 0

        // handle numeric vs string comparison - number < string - (Kyle Adams)
        if (isNaN(+oFxNcL) !== isNaN(+oFyNcL)) return (isNaN(+oFxNcL)) ? GREATER : SMALLER

        // rely on string comparison if different types - i.e. '02' < 2 != '02' < '2'
        else if (typeof oFxNcL !== typeof oFyNcL) {
          oFxNcL += ''
          oFyNcL += ''
        }

        if (oFxNcL < oFyNcL) return SMALLER
        if (oFxNcL > oFyNcL) return GREATER
      }

      return EQUAL
    }
  },

  // Return a sanitised version of a free-text phrase query string.
  sanitisePhraseQuery (phrase: string) {
    // Removes selected punctuation that is adjacent to space or string beginning/end,
    // excerpt for quotes, which can be used to specify an explicit term search.
    const r1 = new RegExp(`(?:^|\\s)${PUNCTUATION_RE_OPENING.source}`, 'g')
    const r2 = new RegExp(`${PUNCTUATION_RE_CLOSING.source}(?:$|\\s)`, 'g')
    return phrase
      .replace(r1, '')
      .replace(r2, '')
      // Remove everything that is only punctuation. If there are quotes
      // on either side remove those too. This was added in sc-43397 to
      // prevent searches `"%"` and `*.*` from crashing the query
      // backend.
      // https://regex101.com/r/zqUfU3/1
      .replace(/^["']*\W+["']*$/, '')

      // Remove double quotes on their own
      .replace('""', '')

      // Remove single double quote
      .replace('"\b\b', '')
      .replace('\b\b"', '')

      // Remove redundant opening quotes
      .replace(/(?:^)\"\"+/g, '"')
      // Remove redundant closing quotes
      .replace(/\"\"+(?:$)/g, '"')

      // Auto-close a single opening quote
      .replace(/(^\"[^"]+$)/, '$1"')
      // Remove an orphaned trailing quote
      .replace(/(^[^"]+)\"$/, '$1')

      // Remove double quotes on their own if we have ended up back in this situation
      .replace('""', '')

      // Remove redundant whitespace in phrases
      .trim().split(/\s/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      .join(' ')
  },

  /**
   * Is this browser IE 🤢
   */
  /* istanbul ignore next */
  isIE () {
    let ua = window.navigator.userAgent
    return ua.indexOf('MSIE') > 0 || ua.indexOf('Trident/') > 0
  },


  /**
   * Parse segments from string in either comma or line separated format.
   * Note: Allows duplicates.
   */
  parseSegmentsFromStr (value: string) {
    let segments = []
    let m
    let vm = value.match(LINES_WITH_CONTENT_RE)
    if (vm && vm.length > 1) {
      // multiple lines
      while ((m = SEGMENTS_LINE_RE.exec(value)) !== null) {
        segments.push(m[1].trim())
      }
    } else {
      // single line
      while ((m = SEGMENTS_COMMA_RE.exec(value.split(/\r?\n/)[0])) !== null) {
        segments.push(m[1].trim())
      }
    }
    return segments
  },

  /**
   * Generate a UUID
   */
  uuid,
  isEmpty (obj: any) {
    let hasData = !!obj && (
      (obj.size > 0) || (Object.keys(obj).length > 0)
    )
    return !hasData
  },
  cloneDeep: _.cloneDeep,
  /**
   * Link reversal
   */
  hashids,
  sleep,
}
